import styled from 'styled-components'
import { t } from 'ttag'
import isEmail from 'validator/lib/isEmail'
import { useMemo, useCallback, useEffect, useContext } from 'react'
import { useDispatch } from 'react-redux'
import isFunction from 'lodash/isFunction'

import { features } from '@h2/components/media'
import { NAVIANCEBLUE, SLB, PG } from '@h2/components/colors'
import { Heading } from '@h2/components/typography/heading'
import { Text } from '@h2/components/typography/text'
import { Field } from '@h2/components/inputs/field'
import { TextInput } from '@h2/components/inputs/text'
import { Button } from '@h2/components/buttons/button'
import { Alert } from '@h2/components/display/alert'
import { useForm } from '@h2/common/components/hooks/useForm'
import { Link } from '@h2/common/components/link'
import { Card } from '@h2/common/components/card'
import { isPublicEmail } from '@h2/common/utils/isPublicEmail'
import { openSigninDialog } from '@main/actions/signinOrRegisterDialog'
import { Globals } from '@h2/globals'

import { props } from './props'
import { EmailForm as Form } from './components'

import { Create } from '../highlights/create'
import { Navigator } from '../highlights/navigator'
import { Controls } from '../controls'
import { Submit } from '../submit'
import { Back } from '../back'

const ButtonLink = styled(Button)`
  text-decoration: underline;
  margin-top: 15px;

  @media (${features.minWidth.laptop}) {
    margin-top: 35px;
  }
`
const Email = ({
  inDialog,
  properties,
  validate,
  isValidating,
  renderPanel,
  formErrors,
  serverErrorMsg,
  customDetourHandler
}) => {
  const dispatch = useDispatch()
  const { isNavianceUser } = useContext(Globals)
  const isExplorer = useMemo(() => properties.isExplorer ?? true, [properties.isExplorer])
  const { register, error, isInvalid, handleOnSubmit, setValidation, setValue } = useForm(
    {
      name(form) {
        if (!form.name?.trim()) {
          return t`Email is required`
        }

        if (!isEmail(form.name)) {
          return t`Invalid email address`
        }
      }
    }
  )
  const onSubmit = useCallback(
    form => {
      if (!isExplorer && isPublicEmail(form.name)) {
        renderPanel('emailWarning', {
          name: form.name,
          noEmail: false
        })
      } else {
        validate({ ...form, noEmail: false })
      }
    },
    [validate, isExplorer, renderPanel]
  )
  const detourLinkProps = useMemo(
    () => ({
      onClick:
        !inDialog && !isFunction(customDetourHandler)
          ? undefined
          : evt => {
              evt.preventDefault()

              if (isFunction(customDetourHandler)) {
                customDetourHandler()
              } else {
                dispatch(openSigninDialog('signIn'))
              }
            }
    }),
    [dispatch, inDialog, customDetourHandler]
  )
  const headingTxt = isNavianceUser
    ? t`Enter your email address to get started`
    : !isExplorer
    ? t`As you are a Navigator you need an email address associated with your account.`
    : t`To get started, tell us your email address`

  useEffect(() => {
    if (properties.name) {
      setValue({ name: properties.name })
    }
  }, [properties.name, setValue])

  useEffect(() => {
    if (formErrors) {
      setValidation(formErrors)
    }
  }, [formErrors, setValidation])
  const isBelow13 = properties.isBelow13

  return (
    <Card useDetour inDialog={inDialog}>
      <Card.Highlight color={isNavianceUser ? NAVIANCEBLUE : isExplorer ? PG : SLB}>
        {isExplorer ? (
          <Create isNamePanel type={isNavianceUser ? 'navianceUser' : 'unknown'} />
        ) : (
          <Navigator />
        )}
      </Card.Highlight>
      <Card.Content>
        <Form onSubmit={handleOnSubmit(onSubmit)}>
          {serverErrorMsg && (
            <Alert
              type="error"
              message={serverErrorMsg}
              margin="0 0 15px 0"
              width="100%"
            />
          )}
          <Heading level="2" align="center">
            {headingTxt}
          </Heading>
          <Field
            isRequired
            width="100%"
            margin="15px 0 0 0"
            label={t`Email`}
            desc={error.name}
            status={error.name ? 'error' : 'default'}>
            <TextInput size="small" {...register('name', { required: true })} />
          </Field>
          <ButtonLink
            variant="link"
            size="large"
            display="monochrome"
            color={isNavianceUser ? PG : ''}
            // Need confirmation on if we can do this.
            onClick={() => isBelow13 ? renderPanel('username') : validate({ ...properties, name: null, noEmail: true })}>
            {t`I don't have an email address`}
          </ButtonLink>
          <Controls useControls>
            {!isExplorer && <Back onClick={() => renderPanel('mainType')} />}
            <Submit
              text={t`Next`}
              isLoading={isValidating}
              isDisabled={isInvalid || isValidating || Boolean(serverErrorMsg)}
            />
          </Controls>
        </Form>
      </Card.Content>
      {isExplorer && (
        <Card.Detour>
          <Text>
            {t`Already have an account?`}{' '}
            <Link href="/signin" {...detourLinkProps}>{t`Sign in here`}</Link>
          </Text>
        </Card.Detour>
      )}
    </Card>
  )
}

Email.propTypes = { ...props }

export { Email }
